import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { cn } from "@/lib/utils";
import { SnapshotCompare } from "./SnapshotCompare";
import { 
  Gauge,
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Upload,
  Search,
  Download,
  Zap,
  Image as ImageIcon,
  MousePointer,
  Timer,
  LayoutPanelTop,
  Info,
  Folder,
  Smartphone
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Score Meter Component for circular progress
const ScoreMeter = ({ score, label, size = 48, showLabel = true }) => {
  const strokeWidth = 3;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getColor = (score) => {
    if (score >= 90) return { stroke: "#22c55e", text: "text-green-500", bg: "bg-green-500/10" };
    if (score >= 50) return { stroke: "#f97316", text: "text-orange-500", bg: "bg-orange-500/10" };
    return { stroke: "#ef4444", text: "text-red-500", bg: "bg-red-500/10" };
  };
  
  const getCategory = (score) => {
    if (score >= 90) return "Good";
    if (score >= 50) return "Needs Work";
    return "Poor";
  };
  
  const colors = getColor(score);
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#27272a"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn("text-sm font-bold font-mono", colors.text)}>
            {Math.round(score)}
          </span>
        </div>
      </div>
      {showLabel && (
        <>
          <span className="text-[9px] text-zinc-500 font-mono mt-0.5">{label}</span>
          <span className={cn("text-[8px] font-mono px-1 py-0.5 rounded-sm", colors.bg, colors.text)}>
            {getCategory(score)}
          </span>
        </>
      )}
    </div>
  );
};

// Metric Badge with rating colors
const MetricBadge = ({ label, value, unit, rating, icon: Icon }) => {
  const getRatingColor = (rating) => {
    switch (rating?.toUpperCase()) {
      case "FAST": return "bg-green-500/10 text-green-500 border-green-500/30";
      case "AVERAGE": return "bg-orange-500/10 text-orange-500 border-orange-500/30";
      case "SLOW": return "bg-red-500/10 text-red-500 border-red-500/30";
      default: return "bg-zinc-800 text-zinc-400 border-zinc-700";
    }
  };
  
  const formatValue = (val, unit) => {
    if (unit === "ms") {
      if (val >= 1000) return `${(val / 1000).toFixed(1)}s`;
      return `${Math.round(val)}ms`;
    }
    if (unit === "cls") return val.toFixed(3);
    return val;
  };
  
  return (
    <div className={cn(
      "flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs font-mono",
      getRatingColor(rating)
    )}>
      {Icon && <Icon className="w-3 h-3" />}
      <span>{label}:</span>
      <span className="font-bold">{formatValue(value, unit)}</span>
    </div>
  );
};

// Site Card Component
const SiteCard = ({ site, onScan, onDelete, onSelect, onRescanErrors, isSelected, errorCount }) => {
  const getStatusIcon = () => {
    switch (site.status) {
      case "COMPLETED":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "SCANNING":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "ERROR":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-500" />;
    }
  };

  const progress = site.total_pages > 0 
    ? Math.round((site.scanned_pages / site.total_pages) * 100) 
    : 0;

  return (
    <div 
      onClick={() => onSelect(site)}
      className={cn(
        "bg-zinc-900 border rounded-sm p-4 cursor-pointer transition-all hover:border-zinc-600",
        isSelected ? "border-green-500/50 bg-zinc-900/80" : "border-zinc-800"
      )}
      data-testid={`pagespeed-site-card-${site.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-white font-mono text-sm truncate max-w-[180px]" title={site.name}>
            {site.name}
          </span>
        </div>
      </div>
      
      <p className="text-[10px] text-zinc-600 font-mono mb-2 truncate" title={site.domain}>
        {site.domain}
      </p>
      
      {/* Performance Score */}
      {site.avg_performance_score > 0 && site.status !== "SCANNING" && (
        <div className="flex justify-center gap-4 mb-3">
          <ScoreMeter score={site.avg_performance_score || 0} label="Performance" size={52} />
        </div>
      )}

      {site.status === "SCANNING" && (
        <div className="mb-3">
          <div className="flex justify-between text-[10px] text-zinc-500 font-mono mb-1">
            <span>Analyzing...</span>
            <span>{site.scanned_pages} / {site.total_pages} pages</span>
          </div>
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Speed Distribution Mini */}
      {site.status !== "SCANNING" && (site.fast_pages > 0 || site.average_pages > 0 || site.slow_pages > 0) && (
        <div className="flex gap-1 text-[9px] font-mono mb-2">
          <span className="text-green-500">{site.fast_pages || 0} Fast</span>
          <span className="text-zinc-600">|</span>
          <span className="text-orange-500">{site.average_pages || 0} Avg</span>
          <span className="text-zinc-600">|</span>
          <span className="text-red-500">{site.slow_pages || 0} Slow</span>
        </div>
      )}

      {site.last_scan_at && (
        <p className="text-[10px] text-zinc-600 font-mono mb-2">
          Last: {new Date(site.last_scan_at).toLocaleString()}
        </p>
      )}

      <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <SnapshotCompare 
          siteId={site.id} 
          siteName={site.name} 
          monitorType="pagespeed"
          buttonClassName="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7 border-zinc-700"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onScan(site.id, false)}
          disabled={site.status === "SCANNING"}
          className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7"
          title="Crawl new pages only"
        >
          <RefreshCw className={cn("w-3 h-3 mr-1", site.status === "SCANNING" && "animate-spin")} />
          New
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onScan(site.id, true)}
          disabled={site.status === "SCANNING"}
          className="flex-1 text-blue-400 hover:text-blue-300 hover:bg-blue-950/30 rounded-sm font-mono text-[10px] h-7"
          title="Full rescan all pages"
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Full
        </Button>
        {errorCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRescanErrors(site.id)}
            disabled={site.status === "SCANNING"}
            className="flex-1 text-amber-400 hover:text-amber-300 hover:bg-amber-950/30 rounded-sm font-mono text-[10px] h-7"
            title={`Rescan ${errorCount} error pages`}
          >
            <AlertTriangle className="w-3 h-3 mr-1" />
            Retry ({errorCount})
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(site.id)}
          className="text-red-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm font-mono text-[10px] h-7 px-2"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

// Page Row Component
const PageRow = ({ page, isExpanded, onToggle }) => {
  const getCategoryColor = (category) => {
    switch (category) {
      case "fast": return "text-green-500";
      case "average": return "text-orange-500";
      default: return "text-red-500";
    }
  };

  // Mobile-only metrics
  const perfScore = page.performance_score || 0;
  const accessScore = page.accessibility_score || 0;
  const bpScore = page.best_practices_score || 0;
  const seoScore = page.seo_score || 0;
  const fcp = page.fcp || 0;
  const fcpRating = page.fcp_rating || '';
  const lcp = page.lcp || 0;
  const lcpRating = page.lcp_rating || '';
  const tbt = page.tbt || 0;
  const tbtRating = page.tbt_rating || '';
  const cls = page.cls || 0;
  const clsRating = page.cls_rating || '';
  const speedIndex = page.speed_index || 0;
  const speedCategory = page.speed_category || 'slow';

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-zinc-800/50"
        onClick={onToggle}
        data-testid={`pagespeed-page-row-${page.id}`}
      >
        <button className="text-zinc-500">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <p className="text-xs text-zinc-300 font-mono truncate" title={page.url}>
            {page.url.replace(/^https?:\/\/[^/]+/, '')}
          </p>
        </div>
        
        {/* Mobile score only */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-1" title="Mobile Performance Score">
            <Smartphone className="w-3 h-3 text-blue-400" />
            <ScoreMeter score={perfScore} label="" size={32} showLabel={false} />
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <MetricBadge label="FCP" value={page.fcp} unit="ms" rating={page.fcp_rating} icon={Zap} />
          <MetricBadge label="LCP" value={page.lcp} unit="ms" rating={page.lcp_rating} icon={ImageIcon} />
        </div>
        
        <a 
          href={page.url} 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-zinc-500 hover:text-white p-1"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
      
      {isExpanded && (
        <div className="border-t border-zinc-800 p-4 bg-zinc-950/50">
          {/* Speed Category Badge */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-zinc-400 flex items-center gap-1">
              <Smartphone className="w-3 h-3 text-blue-400" /> Mobile Performance
            </span>
            <span className={cn("text-xs font-mono capitalize px-2 py-1 rounded bg-blue-500/20 text-blue-400")}>
              {speedCategory}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Core Web Vitals */}
            <div>
              <h4 className="text-xs text-zinc-400 font-mono mb-3 flex items-center gap-1">
                <Gauge className="w-3 h-3" /> Core Web Vitals
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-900 p-3 rounded-sm border border-zinc-800">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-zinc-500 font-mono">FCP</span>
                    <span className={cn("text-[9px] font-mono px-1.5 py-0.5 rounded", 
                      fcpRating === "FAST" ? "bg-green-500/20 text-green-400" :
                      fcpRating === "AVERAGE" ? "bg-orange-500/20 text-orange-400" :
                      "bg-red-500/20 text-red-400"
                    )}>{fcpRating || 'N/A'}</span>
                  </div>
                  <p className="text-lg font-bold text-white font-mono">
                    {fcp >= 1000 ? `${(fcp / 1000).toFixed(1)}s` : `${Math.round(fcp)}ms`}
                  </p>
                  <p className="text-[9px] text-zinc-600">First Contentful Paint</p>
                </div>
                
                <div className="bg-zinc-900 p-3 rounded-sm border border-zinc-800">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-zinc-500 font-mono">LCP</span>
                    <span className={cn("text-[9px] font-mono px-1.5 py-0.5 rounded", 
                      lcpRating === "FAST" ? "bg-green-500/20 text-green-400" :
                      lcpRating === "AVERAGE" ? "bg-orange-500/20 text-orange-400" :
                      "bg-red-500/20 text-red-400"
                    )}>{lcpRating || 'N/A'}</span>
                  </div>
                  <p className="text-lg font-bold text-white font-mono">
                    {lcp >= 1000 ? `${(lcp / 1000).toFixed(1)}s` : `${Math.round(lcp)}ms`}
                  </p>
                  <p className="text-[9px] text-zinc-600">Largest Contentful Paint</p>
                </div>
                
                <div className="bg-zinc-900 p-3 rounded-sm border border-zinc-800">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-zinc-500 font-mono">TBT</span>
                    <span className={cn("text-[9px] font-mono px-1.5 py-0.5 rounded", 
                      tbtRating === "FAST" ? "bg-green-500/20 text-green-400" :
                      tbtRating === "AVERAGE" ? "bg-orange-500/20 text-orange-400" :
                      "bg-red-500/20 text-red-400"
                    )}>{tbtRating || 'N/A'}</span>
                  </div>
                  <p className="text-lg font-bold text-white font-mono">
                    {tbt >= 1000 ? `${(tbt / 1000).toFixed(1)}s` : `${Math.round(tbt)}ms`}
                  </p>
                  <p className="text-[9px] text-zinc-600">Total Blocking Time</p>
                </div>
                
                <div className="bg-zinc-900 p-3 rounded-sm border border-zinc-800">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-zinc-500 font-mono">CLS</span>
                    <span className={cn("text-[9px] font-mono px-1.5 py-0.5 rounded", 
                      clsRating === "FAST" ? "bg-green-500/20 text-green-400" :
                      clsRating === "AVERAGE" ? "bg-orange-500/20 text-orange-400" :
                      "bg-red-500/20 text-red-400"
                    )}>{clsRating || 'N/A'}</span>
                  </div>
                  <p className="text-lg font-bold text-white font-mono">
                    {cls?.toFixed(3) || '0.000'}
                  </p>
                  <p className="text-[9px] text-zinc-600">Cumulative Layout Shift</p>
                </div>
              </div>
            </div>
            
            {/* Additional Scores & Metrics */}
            <div>
              <h4 className="text-xs text-zinc-400 font-mono mb-3 flex items-center gap-1">
                <LayoutPanelTop className="w-3 h-3" /> Lighthouse Scores
              </h4>
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div className="text-center">
                  <ScoreMeter score={perfScore} size={44} showLabel={false} />
                  <div className="text-[9px] text-zinc-500 mt-1">Performance</div>
                </div>
                <div className="text-center">
                  <ScoreMeter score={accessScore} size={44} showLabel={false} />
                  <div className="text-[9px] text-zinc-500 mt-1">Accessibility</div>
                </div>
                <div className="text-center">
                  <ScoreMeter score={bpScore} size={44} showLabel={false} />
                  <div className="text-[9px] text-zinc-500 mt-1">Best Practices</div>
                </div>
                <div className="text-center">
                  <ScoreMeter score={seoScore} size={44} showLabel={false} />
                  <div className="text-[9px] text-zinc-500 mt-1">SEO</div>
                </div>
              </div>
              
              {/* Speed Index & Page Size */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-zinc-900 p-2 rounded-sm border border-zinc-800">
                  <div className="text-[10px] text-zinc-500 font-mono">Speed Index</div>
                  <p className="text-sm font-bold text-white font-mono">
                    {speedIndex >= 1000 ? `${(speedIndex / 1000).toFixed(1)}s` : `${Math.round(speedIndex)}ms`}
                  </p>
                </div>
                <div className="bg-zinc-900 p-2 rounded-sm border border-zinc-800">
                  <div className="text-[10px] text-zinc-500 font-mono">Page Size</div>
                  <p className="text-sm font-bold text-white font-mono">
                    {page.total_byte_weight > 1048576 
                      ? `${(page.total_byte_weight / 1048576).toFixed(1)} MB`
                      : `${Math.round(page.total_byte_weight / 1024)} KB`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Opportunities Section */}
          {page.opportunities && page.opportunities.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs text-zinc-400 font-mono mb-2 flex items-center gap-1">
                <Info className="w-3 h-3" /> Opportunities to Improve
              </h4>
              <div className="space-y-1">
                {page.opportunities.slice(0, 5).map((opp, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs font-mono bg-zinc-900 p-2 rounded-sm border border-zinc-800">
                    <span className="text-zinc-400 flex-1">{opp.title}</span>
                    {opp.savings && (
                      <span className="text-green-500">{opp.savings}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Error Message */}
          {page.scan_error && page.error_message && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
              <p className="text-xs text-red-400 font-mono flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {page.error_message}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main PageSpeed Monitor Component
export const PageSpeedMonitor = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [siteErrorCounts, setSiteErrorCounts] = useState({});
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [siteName, setSiteName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageFilter, setPageFilter] = useState("all");
  const [selectedSitemapType, setSelectedSitemapType] = useState("all");
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("all");

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
    } catch (e) {
      console.error("Failed to fetch groups:", e);
    }
  }, []);

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const filteredSites = sites.filter((site) => {
    // Group filter
    if (filterGroup !== "all") {
      if (!site.groups || !site.groups.includes(filterGroup)) {
        return false;
      }
    }
    // Search filter
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      site.name?.toLowerCase().includes(query) ||
      site.domain?.toLowerCase().includes(query)
    );
  });

  const fetchErrorCounts = useCallback(async (siteList) => {
    const counts = {};
    for (const site of siteList) {
      // Fetch error counts for COMPLETED or ERROR status sites
      if (site.status === 'COMPLETED' || site.status === 'ERROR') {
        try {
          const response = await axios.get(`${API}/pagespeed-monitor/${site.id}/stats`);
          counts[site.id] = response.data.errors || 0;
        } catch (e) {
          counts[site.id] = 0;
        }
      }
    }
    setSiteErrorCounts(counts);
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pagespeed-monitor`);
      setSites(response.data);
      fetchErrorCounts(response.data);
      
      if (response.data.length > 0 && !selectedSite) {
        setSelectedSite(response.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch sites:", e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSite, fetchErrorCounts]);

  const fetchStats = useCallback(async () => {
    if (!selectedSite) return;
    
    try {
      const response = await axios.get(`${API}/pagespeed-monitor/${selectedSite.id}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  }, [selectedSite]);

  const fetchPages = useCallback(async () => {
    if (!selectedSite) return;
    
    setIsLoadingPages(true);
    try {
      const params = new URLSearchParams();
      if (pageFilter !== "all") params.append('filter', pageFilter);
      if (selectedSitemapType && selectedSitemapType !== "all") params.append('sitemap_type', selectedSitemapType);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/pagespeed-monitor/${selectedSite.id}/pages${queryString}`);
      setPages(response.data);
    } catch (e) {
      console.error("Failed to fetch pages:", e);
    } finally {
      setIsLoadingPages(false);
    }
  }, [selectedSite, pageFilter, selectedSitemapType]);

  useEffect(() => {
    fetchSites();
    fetchGroups();
    const interval = setInterval(fetchSites, 10000);
    return () => clearInterval(interval);
  }, [fetchSites, fetchGroups]);

  // Reset sitemap type filter only when site changes
  useEffect(() => {
    if (selectedSite) {
      setSelectedSitemapType("all");
    }
  }, [selectedSite?.id]);

  // Fetch stats and pages when site, filter, or sitemap type changes
  useEffect(() => {
    if (selectedSite) {
      fetchStats();
      fetchPages();
    }
  }, [selectedSite, pageFilter, selectedSitemapType, fetchStats, fetchPages]);

  const handleAddSite = async () => {
    if (!sitemapUrl) {
      toast.error("Please enter a sitemap URL");
      return;
    }

    setIsAdding(true);
    try {
      const response = await axios.post(`${API}/pagespeed-monitor`, {
        sitemap_url: sitemapUrl,
        name: siteName,
        groups: selectedGroups
      });
      toast.success("Site added - PageSpeed analysis started");
      setSitemapUrl("");
      setSiteName("");
      setSelectedGroups([]);
      fetchSites();
      setSelectedSite(response.data);
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to add site";
      toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleScan = async (siteId, full = false) => {
    try {
      await axios.post(`${API}/pagespeed-monitor/${siteId}/scan?full=${full}`);
      toast.success(full ? "Full PageSpeed scan started" : "PageSpeed scan started");
      fetchSites();
    } catch (e) {
      toast.error("Failed to start scan");
    }
  };

  const handleRescanErrors = async (siteId) => {
    try {
      const response = await axios.post(`${API}/pagespeed-monitor/${siteId}/rescan-errors`);
      if (response.data.error_count > 0) {
        toast.success(`Rescanning ${response.data.error_count} error pages`);
      } else {
        toast.info("No error pages to rescan");
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to start rescan");
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm("Delete this site and all its PageSpeed data?")) return;
    
    try {
      await axios.delete(`${API}/pagespeed-monitor/${siteId}`);
      toast.success("Site deleted");
      if (selectedSite?.id === siteId) {
        setSelectedSite(null);
        setPages([]);
        setStats(null);
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to delete site");
    }
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/pagespeed-monitor/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { added, skipped, errors } = response.data;
      
      if (added?.length > 0) toast.success(`Imported ${added.length} site(s)`);
      if (skipped?.length > 0) toast.info(`Skipped ${skipped.length} duplicate(s)`);
      if (errors?.length > 0) toast.error(`${errors.length} URL(s) had errors`);
      
      fetchSites();
    } catch (e) {
      toast.error("Failed to import CSV - endpoint may not exist yet");
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  const filters = [
    { id: "all", label: "All Pages" },
    { id: "fast", label: "Fast (90+)" },
    { id: "average", label: "Average (50-89)" },
    { id: "slow", label: "Slow (<50)" },
    { id: "errors", label: "Errors" },
  ];

  return (
    <div className="p-6" data-testid="pagespeed-monitor">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-2">
          <Gauge className="w-6 h-6 text-green-500" />
          PageSpeed Monitoring
        </h1>
        <p className="text-zinc-500 text-sm font-mono mt-1">
          Core Web Vitals: Performance, FCP, LCP, TBT, CLS via Google PageSpeed API
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Panel - Sites List */}
        <div className="col-span-4">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
            <h2 className="text-sm font-mono text-white mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4 text-green-500" />
              MONITORED SITES
            </h2>

            {/* Add Site Form */}
            <div className="space-y-2 mb-4">
              <Input
                placeholder="Site name (optional)"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                data-testid="pagespeed-site-name-input"
              />
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/sitemap.xml"
                  value={sitemapUrl}
                  onChange={(e) => setSitemapUrl(e.target.value)}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                  data-testid="pagespeed-sitemap-input"
                />
                <Button
                  onClick={handleAddSite}
                  disabled={isAdding || !sitemapUrl}
                  className="bg-green-600 hover:bg-green-700 text-white font-mono text-xs h-8 px-3"
                  data-testid="pagespeed-add-btn"
                >
                  {isAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Group Selection */}
              {groups.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-zinc-500 font-mono">Groups:</span>
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => toggleGroupSelection(group.id)}
                      className={cn(
                        "flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors",
                        selectedGroups.includes(group.id)
                          ? "bg-zinc-700 text-white"
                          : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                      )}
                    >
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: group.color }} />
                      {group.name}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Import CSV */}
              <div className="flex gap-2">
                <label className="flex-1">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvImport}
                    className="hidden"
                    data-testid="pagespeed-csv-input"
                  />
                  <Button
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 font-mono text-xs h-8"
                    disabled={isImporting}
                    asChild
                  >
                    <span>
                      <Upload className="w-3 h-3 mr-1" />
                      {isImporting ? "Importing..." : "Import CSV"}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Search and Group Filter */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  placeholder="Search sites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                  data-testid="pagespeed-search-input"
                />
              </div>
              {groups.length > 0 && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded-sm px-2 py-1 text-[10px] text-zinc-300 font-mono h-8"
                  data-testid="pagespeed-group-filter"
                >
                  <option value="all">All Groups</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Sites List */}
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 rounded-sm p-4 animate-pulse">
                    <div className="h-4 bg-zinc-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-zinc-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : filteredSites.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 font-mono text-sm">
                <Gauge className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No sites yet</p>
                <p className="text-xs mt-1">Add a sitemap URL to start</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[calc(100vh-420px)] overflow-y-auto pr-1">
                {filteredSites.map((site) => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    onScan={handleScan}
                    onDelete={handleDelete}
                    onSelect={setSelectedSite}
                    onRescanErrors={handleRescanErrors}
                    isSelected={selectedSite?.id === site.id}
                    errorCount={siteErrorCounts[site.id] || 0}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Site Details */}
        <div className="col-span-8">
          {selectedSite ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-mono text-white flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-green-500" />
                  {selectedSite.name}
                </h2>
                <span className="text-xs text-zinc-500 font-mono">
                  {selectedSite.total_pages} pages
                </span>
              </div>

              {/* Stats Grid */}
              {stats && (
                <div className="grid grid-cols-5 gap-3 mb-4">
                  {/* Performance Average */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3 flex flex-col items-center justify-center">
                    <ScoreMeter 
                      score={stats.avg_performance || 0} 
                      label="Avg Perf" 
                      size={56}
                    />
                  </div>
                  
                  {/* Speed Distribution */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1">SPEED</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-green-500">Fast (90+)</span>
                        <span className="text-white">{stats.fast || 0}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-orange-500">Average</span>
                        <span className="text-white">{stats.average || 0}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-red-500">Slow (&lt;50)</span>
                        <span className="text-white">{stats.slow || 0}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Core Web Vitals - Fast Count */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1">FAST METRICS</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">FCP</span>
                        <span className="text-green-500">{stats.fcp_fast || 0}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">LCP</span>
                        <span className="text-green-500">{stats.lcp_fast || 0}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">CLS</span>
                        <span className="text-green-500">{stats.cls_fast || 0}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">TBT</span>
                        <span className="text-green-500">{stats.tbt_fast || 0}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Average Metrics */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1">AVG METRICS</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">FCP</span>
                        <span className="text-white">{stats.avg_fcp ? `${(stats.avg_fcp/1000).toFixed(1)}s` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">LCP</span>
                        <span className="text-white">{stats.avg_lcp ? `${(stats.avg_lcp/1000).toFixed(1)}s` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">CLS</span>
                        <span className="text-white">{stats.avg_cls?.toFixed(3) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">TBT</span>
                        <span className="text-white">{stats.avg_tbt ? `${Math.round(stats.avg_tbt)}ms` : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Totals */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1">PAGES</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">Total</span>
                        <span className="text-white">{stats.total || 0}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">Scanned</span>
                        <span className="text-white">{stats.total || 0}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-zinc-400">Errors</span>
                        <span className={stats.errors > 0 ? "text-red-500" : "text-white"}>{stats.errors || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Filter Tabs */}
              <div className="flex gap-1 mb-4 overflow-x-auto items-center">
                {filters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setPageFilter(f.id)}
                    className={cn(
                      "px-3 py-1.5 font-mono text-xs rounded-sm transition-colors whitespace-nowrap",
                      pageFilter === f.id 
                        ? "bg-zinc-700 text-white" 
                        : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                    )}
                    data-testid={`pagespeed-filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
                <div className="flex-1" />
                {/* Sitemap Type Filter */}
                {selectedSite?.sitemap_types && selectedSite.sitemap_types.length > 1 && (
                  <select
                    value={selectedSitemapType}
                    onChange={(e) => setSelectedSitemapType(e.target.value)}
                    className="bg-zinc-900 border border-zinc-700 rounded-sm text-xs font-mono px-2 py-1 text-zinc-300 focus:outline-none focus:border-zinc-500"
                    data-testid="sitemap-type-filter"
                  >
                    <option value="all">All Types</option>
                    {selectedSite.sitemap_types.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Pages List */}
              <div className="space-y-2 max-h-[calc(100vh-450px)] overflow-y-auto">
                {isLoadingPages ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 animate-pulse">
                      <div className="h-4 bg-zinc-800 rounded w-3/4" />
                    </div>
                  ))
                ) : pages.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500 font-mono text-sm">
                    <p>No pages in this category</p>
                  </div>
                ) : (
                  pages.map((page) => (
                    <PageRow
                      key={page.id}
                      page={page}
                      isExpanded={expandedPage === page.id}
                      onToggle={() => setExpandedPage(expandedPage === page.id ? null : page.id)}
                    />
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8 flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center text-zinc-500 font-mono">
                <Gauge className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-sm">Select a site to view PageSpeed details</p>
                <p className="text-xs mt-1">or add a new sitemap URL</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageSpeedMonitor;
